/**
 * 페이지별 SEO 메타 정의.
 *
 * 키워드 맵의 SSOT 는 `claudeFiles/judahsWeb/SEO_PLAN.md` 다.
 * 여기 문구를 고칠 때는 그 문서의 표도 함께 갱신한다.
 *
 * title 은 60자, description 은 155자 안쪽을 목표로 한다.
 * 그 이상은 검색 결과에서 잘린다.
 */

export interface SeoEntryType {
	/** 라우트 경로. canonical 생성에 쓴다. */
	path: string
	title: string
	description: string
	/** 지정하지 않으면 사이트 기본 OG 이미지 */
	ogImage?: string
	/** true 면 robots 에 noindex 를 넣는다 */
	noindex?: boolean
	/**
	 * 정본이 다른 경로에 있을 때 그 경로. 지정하면 `noindex` 여도 canonical 을 낸다.
	 *
	 * `/shop/{code}` 처럼 같은 제품에 URL 이 둘인 경우에 쓴다 — 거래 화면은 색인하지
	 * 않지만, 색인 판단을 하는 크롤러에게 정본이 `/products/{code}` 라는 것은 알려야
	 * 한다. 지정하지 않은 noindex 페이지(404·백오피스·soft 404)는 canonical 을 내지
	 * 않는다. 존재하지 않는 경로가 자기 자신을 정본으로 지목하게 되기 때문이다.
	 */
	canonicalPath?: string
}

/**
 * 브랜드·제품의 한국어 표기 (2026-09-21).
 *
 * 사이트 본문은 영어지만, 한국에서는 브랜드를 한글 음역으로 검색한다. 구글은 페이지에
 * 없는 문자열로 그 페이지를 띄우지 않으므로, 이 표기가 **실제 텍스트**(푸터 한 줄)와
 * **구조화 데이터**(`alternateName`) 양쪽에 들어가야 한다. 둘 다 여기를 읽는다.
 *
 * 음역이 확정되지 않은 제품(AirMIDI POG 2 · Dual Switch)은 넣지 않는다 — 표기가 갈리면
 * 브랜드 검색이 쪼개진다(SEO_PLAN §2).
 */
export const SEO_KO = {
	/** 회사·브랜드 */
	brand: '주다스',
	/** 제품 라인 */
	lines: [
		{ name: 'Koinos', ko: '코이노스' },
		{ name: 'Kedem', ko: '케뎀' },
	],
	/** 제품 코드(대문자) → 한국어 표기 */
	products: {
		PLS8: '코이노스 PLS 8',
		KDM6: '케뎀 6',
		KDM4: '케뎀 4',
	} as { [code: string]: string },
	/** 푸터에 내보내는 한 줄. 세 음역이 전부 들어간다 */
	footerLine:
		'주다스 — 기타 페달보드용 MIDI 풋컨트롤러 케뎀, 프로그래머블 루프 스위처 코이노스.',
} as const

export const SEO_SITE = {
	origin: 'https://judahs.io',
	siteName: 'Judahs',
	defaultOgImage: 'https://judahs.io/logo.png',
	locale: 'en_US',
	/** 홈에서 쓰는 기본 문구. 개별 항목이 없는 경로의 fallback 이기도 하다. */
	defaultTitle: 'Judahs — MIDI Controllers & Pedal Switchers for Guitar',
	defaultDescription:
		'Judahs builds MIDI foot controllers and programmable pedal switchers for guitar pedalboards — the Kedem controller line and the Koinos loop switcher line.',
} as const

/**
 * 경로 → 메타.
 *
 * `/main`, `/admin` 하위(백오피스)는 여기에 넣지 않는다. 색인 대상이 아니고,
 * 아래 `getSeoEntry` 가 noindex 로 처리한다.
 */
export const seoMap: { [path: string]: SeoEntryType } = {
	'/': {
		path: '/',
		title: SEO_SITE.defaultTitle,
		description:
			'Touchscreen MIDI foot controllers with wireless MIDI, and programmable pedal switchers with crosspoint matrix routing. Built for working guitarists.',
	},

	'/products': {
		path: '/products',
		title: 'Products — Koinos Pedal Switchers, Kedem MIDI Controllers',
		description:
			'The Judahs lineup by line: Koinos programmable pedal switchers, Kedem MIDI control systems, and utility hardware — AirMIDI POG 2 and Dual Switch.',
	},

	'/shop': {
		path: '/shop',
		title: 'Shop — Buy Judahs MIDI Controllers & Pedal Switchers',
		description:
			'Buy Kedem 6 and Kedem 4 MIDI controllers, AirMIDI POG 2, Dual Switch, and the Koinos PLS 8 pedal switcher. Current pricing and availability.',
	},

	// 2026-09-10 (§16): 한 제품에 URL 이 둘이라 색인 정본을 하나로 못박는다.
	//   `/products/{code}` = 정본 (index + sitemap)
	//   `/shop/{code}`     = 거래 화면 (noindex + sitemap 제외)
	// 아래 4종의 문구는 그대로 두되 noindex 만 붙인다 — 검색 결과에 낼 페이지는 /products 다.
	'/shop/kdm6': {
		path: '/shop/kdm6',
		title: 'Kedem 6 — Touchscreen MIDI Controller, Wireless MIDI',
		description:
			'Six-switch MIDI foot controller with a 3.5" touch display, AirMIDI wireless, and expression pedal input. Controls Quad Cortex, Kemper, Strymon and more.',
		noindex: true,
		canonicalPath: '/products/kdm6',
	},

	'/shop/kdm4': {
		path: '/shop/kdm4',
		title: 'Kedem 4 — Compact Touchscreen MIDI Controller | Judahs',
		description:
			'The compact four-switch Kedem. Same touch display and AirMIDI wireless as the Kedem 6, sized for pedalboards that have run out of room.',
		noindex: true,
		canonicalPath: '/products/kdm4',
	},

	'/shop/amp2': {
		path: '/shop/amp2',
		title: 'AirMIDI POG 2 — Wireless MIDI for the EHX POG2 | Judahs',
		description:
			'Preset switching for the Electro-Harmonix POG2 over wireless MIDI. Existing POG2 MIDI mods are wired; this one is not. Installation service available.',
		noindex: true,
		canonicalPath: '/products/amp2',
	},

	'/shop/ds': {
		path: '/shop/ds',
		title: 'Judahs Dual Switch — TRS Momentary Footswitch | Judahs',
		description:
			'Two momentary footswitches over a single TRS cable. Adds external footswitch control to pedals and amps that expose a TRS jack.',
		noindex: true,
		canonicalPath: '/products/ds',
	},

	// 제품 페이지 = 색인 정본 (§16). 구매 화면 `/shop/{code}` 는 noindex.
	'/products/kdm6': {
		path: '/products/kdm6',
		title: 'Kedem 6 — Touchscreen MIDI Controller, Wireless MIDI',
		description:
			'Six-switch MIDI foot controller with a 3.5" touch display, AirMIDI wireless, and expression pedal input. Controls Quad Cortex, Kemper, Strymon and more.',
	},

	'/products/kdm4': {
		path: '/products/kdm4',
		title: 'Kedem 4 — Compact Touchscreen MIDI Controller | Judahs',
		description:
			'The compact four-switch Kedem. Same touch display and AirMIDI wireless as the Kedem 6, sized for pedalboards that have run out of room.',
	},

	'/products/amp2': {
		path: '/products/amp2',
		title: 'AirMIDI POG 2 — Wireless MIDI for the EHX POG2 | Judahs',
		description:
			'Preset switching for the Electro-Harmonix POG2 over wireless MIDI. Existing POG2 MIDI mods are wired; this one is not. Installation service available.',
	},

	'/products/ds': {
		path: '/products/ds',
		title: 'Judahs Dual Switch — TRS Momentary Footswitch | Judahs',
		description:
			'Two momentary footswitches over a single TRS cable. Adds external footswitch control to Judahs controllers that expose a TRS switch input.',
	},

	// 구매 화면. 색인 정본은 /products/pls8 이라 noindex (§16.1).
	'/shop/pls8': {
		path: '/shop/pls8',
		title: 'Koinos PLS 8 — Buy | Judahs',
		description:
			'Koinos PLS 8 programmable pedal switcher and MIDI controller: prices, options and availability.',
		noindex: true,
		canonicalPath: '/products/pls8',
	},

	'/products/pls8': {
		path: '/products/pls8',
		title: 'Koinos PLS 8 — Touchscreen Pedal Switcher | Judahs',
		description:
			'A programmable pedal switcher: eight loops and a buffered stereo insert on a 100% analog matrix, an 8.88-inch touch display, and ten MIDI footswitches.',
	},

	'/about': {
		path: '/about',
		title: 'About Judahs — Guitar Effect Pedal Builders',
		description:
			'Why Judahs exists. We chose the name first, then built the MIDI controllers and loop switchers that could carry it.',
	},

	'/contact': {
		path: '/contact',
		title: 'Contact Judahs',
		description:
			'Questions about a Judahs product, an order, or the AirMIDI installation service? Get in touch.',
	},

	'/support': {
		path: '/support',
		title: 'Support — Manuals, Software, Compatibility & FAQ | Judahs',
		description:
			'Manuals, Judahs Studio downloads, device compatibility and FAQ for Kedem 6, Kedem 4, AirMIDI POG 2, Dual Switch and Koinos PLS 8.',
	},

	// 2026-09-09 사이트맵 §9 신설. `/forum` 은 2026-09-10 에 forum.judahs.io 로 넘어가 여기서 뺐다.
	// `/works-with` 는 2026-09-11 에 콘텐츠(딕셔너리 색인)가 들어가 색인 정본이 됐다 — sitemap.xml 에도 있다.
	// `/faq` 도 2026-09-11 에 문항이 들어가 색인 정본이 됐다.
	'/works-with': {
		path: '/works-with',
		title: 'Works with — MIDI Device Compatibility | Judahs',
		description:
			'MIDI implementation for the devices Kedem and Koinos control: CC numbers, value ranges and SysEx, by manufacturer — amp modelers, multi-effects and pedals.',
	},

	// 2026-09-11 문항 투입(SITEMAP_PLAN §23) — noindex 를 걷고 sitemap.xml 에 넣었다.
	'/faq': {
		path: '/faq',
		title: 'FAQ — Pre-orders, Shipping & Warranty | Judahs',
		description:
			'Pre-orders, batches, shipping, import duties, payment, order tracking, warranty and firmware for Judahs hardware.',
	},

	'/software': {
		path: '/software',
		title: 'Software — Judahs Studio & Downloads | Judahs',
		description:
			'Desktop software for Judahs gear. Judahs Studio, the offline companion editor for Koinos and Kedem, for macOS and Windows.',
	},

	'/software/judahs-studio': {
		path: '/software/judahs-studio',
		title: 'Judahs Studio — Download | Judahs',
		description:
			'A native editor that runs offline and saves to local files. Edits presets, routing and MIDI on Koinos and Kedem, updates firmware. macOS and Windows.',
	},

	'/manuals': {
		path: '/manuals',
		title: 'Manuals — Koinos PLS 8, Kedem 6, Kedem 4 | Judahs',
		description:
			'User manuals for every Judahs product as PDF, in English, Korean, German, Spanish and Japanese.',
	},

	'/privacy-policy': {
		path: '/privacy-policy',
		title: 'Privacy Policy | Judahs',
		description: 'How Judahs collects, uses, stores and deletes personal information.',
	},

	'/store-policy': {
		path: '/store-policy',
		title: 'Store Policy — Shipping, Returns & Refunds | Judahs',
		description:
			'Shipping, returns, refunds and warranty terms for orders placed on judahs.io.',
	},
}

/** 색인에서 빼는 경로. 개인화 화면과 백오피스. */
const NOINDEX_PREFIXES = ['/main', '/admin', '/cart', '/payment', '/customer', '/auth']

const isNoindexPath = (pathname: string): boolean =>
	NOINDEX_PREFIXES.some(
		prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
	)

/**
 * 현재 경로의 메타를 돌려준다.
 *
 * 맵에 없는 경로는 fallback 을 주되 반드시 `noindex` 를 붙인다.
 * CloudFront 가 없는 경로까지 200 + index.html 로 응답하기 때문에(soft 404),
 * 이 표시가 없으면 존재하지 않는 URL 이 색인된다.
 */
export const getSeoEntry = (pathname: string): SeoEntryType => {
	// 끝 슬래시는 무시하고 찾는다. `/shop/` 와 `/shop` 은 같은 페이지다.
	const normalized =
		pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

	const found = seoMap[normalized]
	if (found !== undefined) {
		return found
	}

	return {
		path: normalized,
		title: SEO_SITE.defaultTitle,
		description: SEO_SITE.defaultDescription,
		noindex: true,
	}
}

export { isNoindexPath }
